/**
 * groupHistoryContainer.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import bluebird from "bluebird";
import Picker from "@emoji-mart/react";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import * as React from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { ButtonGroup, Button, CircularProgress, ClickAwayListener, Popper } from "@mui/material";

import { Membership, PermissionLevel } from "../models/auth";
import { DeviceJoinedMessage, ContentMessage, 
  MessageData, MessageType, DisplayMessage,
  DevicesAddedMessage,
  HistoryPreservationMessage,
  updateCachedMemberNameAsNeeded } from "../models/message";
import { CustomEmoji, ReactionContentType, Reaction,
  EmojiSelection, emojiSelectionToContent } from "../models/reaction";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "./groupNav.context";
import { MessageDisplayComponent } from "./messageDisplay.component";
import { SCAvatar } from "../components/avatar.component";

import { authServiceFactory } from "../services/auth.service";
import { dbServiceFactory } from "../services/db.service";
import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";


interface MessageDay {
  startOfDay: DateTime,
  messages: Array<MessageData>
}

interface GroupHistoryContainerProps {
  scrollingElem: HTMLElement | null,
  loadingGroup: boolean,
  groupID: string,
  myUserID: string,
  authContext: AuthContext,
  doRemoveMessage(msgID: string, frameID: string, threadID: string | undefined): void,
  doRemoveMessageLocally(msgID: string, frameID: string, threadID: string | undefined): void,
  doThread?(threadID: string): void,
  doDownloadFile?(fileID: string, keyID: string): void,
  addReaction?(message: ContentMessage,
    contentID: string, contentType: ReactionContentType,
    content: string): Promise<void>,
  removeReaction?(msg: ContentMessage, reactionID: string, contentID: string, frameID: string | undefined): Promise<void>,
  didScrollToBottom?(): void,
  onError(msg: string): void
}

interface GroupHistoryContainerState {
  loadingMessages: boolean,
  loadedAllMessages: boolean,
  dayMap: {[dateStr: string]: MessageDay},
  openEmojiPicker: boolean,
  anchorEl: HTMLElement | null,
  reactingMessage: ContentMessage | null
}

export class GroupHistoryContainer extends React.Component<GroupHistoryContainerProps, GroupHistoryContainerState> {
  isNearBottom: boolean;

  static contextType: React.Context<GroupNavContext> = GroupNavContext;
  declare context: React.ContextType<typeof GroupNavContext>;

  constructor (props: GroupHistoryContainerProps) {
    super(props);

    this.isNearBottom = true;
    this.onNewMessage = this.onNewMessage.bind(this);
    this.onRemoveMessage = this.onRemoveMessage.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.onShowEmojiPicker = this.onShowEmojiPicker.bind(this);
    this.choseReaction = this.choseReaction.bind(this);
    this.toggleReaction = this.toggleReaction.bind(this);
    this.onReaction = this.onReaction.bind(this);
    this.onRemoveReaction = this.onRemoveReaction.bind(this);

    this.state = {
      loadingMessages: false,
      loadedAllMessages: false,
      dayMap: {},
      openEmojiPicker: false,
      anchorEl: null,
      reactingMessage: null
    };
  }

  componentDidMount (): void {
    const workerService = workerServiceFactory();
    workerService.on("message", this.onNewMessage);
    workerService.on("delmsg", this.onRemoveMessage);
    workerService.on("reaction", this.onReaction);
    workerService.on("remReaction", this.onRemoveReaction);
    this.props.scrollingElem?.addEventListener("scroll", this.handleScroll);
    this.loadMessages(false);
  }

  componentDidUpdate (oldProps: GroupHistoryContainerProps): void {
    if (this.props.groupID !== oldProps.groupID) {
      this.loadMessages(false);
    }
    if (!oldProps.scrollingElem && this.props.scrollingElem) {
      this.props.scrollingElem.addEventListener("scroll", this.handleScroll);
    }
  }

  componentWillUnmount (): void {
    const workerService = workerServiceFactory();
    workerService.off("message", this.onNewMessage);
    workerService.off("delmsg", this.onRemoveMessage);
    workerService.off("reaction", this.onReaction);
    workerService.off("remReaction", this.onRemoveReaction);
    this.props.scrollingElem?.removeEventListener("scroll", this.handleScroll);
  }

  private loadMessages (appending: boolean) {
    if (this.state.loadingMessages) { return; }
    if (!this.props.groupID) {
      this.setState({dayMap: {}});
      return;
    }
    let start = 0;
    let earliestDateShowing: Date | undefined = undefined;
    const limit = 100;

    if (appending) {
      for (const day in this.state.dayMap) {
        if (!Object.prototype.hasOwnProperty.call(this.state.dayMap, day)) {
          continue;
        }
        start += this.state.dayMap[day].messages.length;
        const d = this.state.dayMap[day].startOfDay.toJSDate();
        if (!earliestDateShowing || d < earliestDateShowing) {
          earliestDateShowing = d;
        }
      }
      this.setState({loadingMessages: true});
    } else {
      this.setState({dayMap: {}, loadingMessages: true});
    }

    const displayTZ = authServiceFactory().tz;
    const curGroupID = this.props.groupID;
    const groupService = groupServiceFactory();
    groupService.getGroupMessages(this.props.groupID, start, limit).then(messages => {
      const loadedAll = messages.length < limit;
      const msgMap: {[id: string]: DisplayMessage} = {};
      const dayMap: {[dateStr: string]: MessageDay} = {};
      if (this.props.groupID !== curGroupID) {
        // we changed groups, don't add these messages
        return;
      }
      let minDate: DateTime | undefined = undefined;

      for (const msg of messages) {
        msgMap[msg._id] = msg;
        const dayStart: DateTime = DateTime.isDateTime(msg.created) ? msg.created :
          moment.isMoment(msg.created) ? DateTime.fromJSDate(msg.created.toDate()) :
          DateTime.fromJSDate(msg.created);

        if (!minDate || dayStart < minDate) {
          minDate = dayStart;
        }
        if (displayTZ) {
          dayStart.setZone(displayTZ);
        }
        dayStart.startOf("day");
        const dayStr = dayStart.toFormat("yyyy-MM-dd");

        if (!dayMap[dayStr]) {
          dayMap[dayStr] = {
            startOfDay: dayStart,
            messages: []
          };
        }

        dayMap[dayStr].messages.push(msg);
      }
      for (const dayStr in dayMap) {
        dayMap[dayStr].messages.reverse();
      }

      if (appending) {
        for (const dayStr in dayMap) {
          if (this.state.dayMap[dayStr]) {
            dayMap[dayStr].messages =
              dayMap[dayStr].messages.concat(
                this.state.dayMap[dayStr].messages);
          }
        }
        const ref = this.props.scrollingElem;
        if (ref) {
          this.scrollToHeight(ref, ref.scrollHeight);
        }
      } else {
        this.scrollToBottom();
      }

      this.setState({
        dayMap: {
          ...this.state.dayMap,
          ...dayMap
        },
        //loadingMessages: false,
        loadedAllMessages: loadedAll
      });

      return groupService.getMainReactions(minDate?.toJSDate(), earliestDateShowing).then(reactions => {
        for (const reaction of reactions) {
          const msg = msgMap[reaction.msgID] as ContentMessage;
          if (!msg) { continue; }
          if (!msg.reactions) { msg.reactions = {}; }
          const reactionUser = {
            reactionID: reaction._id,
            userID: reaction.userID,
            firstname: reaction.firstname,
            lastname: reaction.lastname
          };
          if (!msg.reactions[reaction.contentID]) {
            if (!reaction.users) { reaction.users = []; }
            reaction.users.push(reactionUser);
            msg.reactions[reaction.contentID] = reaction;
          } else {
            const existing = msg.reactions[reaction.contentID];
            if (!existing.users) { existing.users = []; }
            existing.users.push(reactionUser);
          }
        }
        this.setState({loadingMessages: false});
      });
    }).then(null, (err: any) => {
      console.error("Couldn't fetch messages for group:",
        this.props.groupID, err);
      this.setState({loadingMessages: false});
      this.props.onError((err as Error).message);
    });
  }

  private handleScroll (/*event: Event*/) {
    const elem = this.props.scrollingElem;
    if (!elem) { return; }
    const voffset = elem.scrollTop || 0;
    const vheight = elem.scrollHeight || 5;
    if (!this.state.loadingMessages && !this.state.loadedAllMessages && voffset <= 0) {
      this.loadMessages(true);
    }
    const nearBottom = (vheight - voffset - elem.offsetHeight - 20) <= 0;
    // console.log("nearbottom:", this.isNearBottom, "h:", vheight,
    //   "off:", voffset, "inner:", elem.offsetHeight,
    //   "off+h:", voffset + elem.offsetHeight);
    if (!this.isNearBottom && nearBottom) {
      this.props.didScrollToBottom && this.props.didScrollToBottom();
    }
    this.isNearBottom = nearBottom;
  }

  private scrollToHeight (ref: Element, height: number) {
    setTimeout((): void => {
      ref.scrollTop = ref.scrollHeight - height;
    }, 2);
  }
  private scrollToBottom () {
    setTimeout((): void => {
      const ref = this.props.scrollingElem;
      if (!ref) { return; }
      ref.scrollTop = ref.scrollHeight;
    }, 50);
  }

  private async onNewMessage (msgID: string, parentID: string | undefined) {
    const dbService = dbServiceFactory();

    const [msg, parent] = parentID ? await bluebird.all([
      dbService.getReply(msgID),
      dbService.getMessage(parentID)
    ]) : await bluebird.all([
      dbService.getMessage(msgID),
      bluebird.resolve(undefined)
    ]);

    if (!msg || !msg._id) {
      return;
    }
    if (msg.groupID !== this.props.groupID) {
      return;
    }

    const displayTZ = authServiceFactory().tz;
    const dayMap = this.state.dayMap;

    if (msg.threadID) {
      if (!parent || parent.msgtype !== MessageType.CONTENT) {
        console.warn("no parent message to update, or wrong msgtype");
        return;
      }
      // created to DateTime
      const creation = DateTime.isDateTime(parent.created) ? parent.created :
        moment.isMoment(parent.created) ? DateTime.fromJSDate(parent.created.toDate()) :
        DateTime.fromJSDate(parent.created);
      parent.created = creation;

      const dayStart = creation;
      displayTZ && dayStart.setZone(displayTZ);
      dayStart.startOf("day");
      const dayStr = dayStart.toFormat("yyyy-MM-dd");
      
      // lastReply to DateTime
      const lastReply = (parent as ContentMessage).lastReply && (
        DateTime.isDateTime((parent as ContentMessage).lastReply) ? parent.lastReply :
        moment.isMoment((parent as ContentMessage).lastReply) ?
          DateTime.fromJSDate((parent.lastReply as moment.Moment).toDate()) :
        DateTime.fromJSDate(parent.lastReply as Date));
      
      (parent as ContentMessage).lastReply = lastReply;

      if (!dayMap[dayStr]) { return; }
      const msgArr = Array.from(dayMap[dayStr].messages);
      const index = msgArr.findIndex(m => parent._id === m._id);
      if (index >= 0) {
        (parent as ContentMessage).reactions = (msgArr[index] as ContentMessage).reactions;
        msgArr[index] = parent;
        dayMap[dayStr].messages = msgArr;
        this.setState({dayMap: dayMap});
      }
      return;
    }

    const creation = DateTime.isDateTime(msg.created) ? msg.created :
        moment.isMoment(msg.created) ? DateTime.fromJSDate(msg.created.toDate()) :
        DateTime.fromJSDate(msg.created);
    msg.created = creation;

    const dayStart = creation;
    displayTZ && dayStart.setZone(displayTZ);
    dayStart.startOf("day");
    const dayStr = dayStart.toFormat("yyyy-MM-dd");

    if (!dayMap[dayStr]) {
      dayMap[dayStr] = { startOfDay: dayStart, messages: [] };
    }

    dayMap[dayStr].messages.push(msg);

    //const voffset = document.scrollingElement?.scrollTop || window.pageYOffset || 0;
    //const vheight = document.scrollingElement?.scrollHeight || 5;

    this.setState({dayMap: dayMap});
    if (this.isNearBottom) {
      this.scrollToBottom();
    }
  }

  private async onRemoveMessage(groupID: string, msgID: string, accountID: string, parentID?: string) {
    void accountID;
    if (groupID !== this.props.groupID) {
      return;
    }
    const [dayStr, msgIndex] = parentID ? this.findMessage(parentID) : this.findMessage(msgID);
    if (dayStr) {
      if (parentID) {
        // updates both replyCount and lastReply
        const updated = await dbServiceFactory().getMessage(parentID);
        if (!updated) { return; }
        this.reloadDay(dayStr, newArr => {
          newArr[newArr.findIndex(msg => msg._id === parentID)] = updated;
        });
      } else {
        this.reloadDay(dayStr, newArr => newArr.splice(msgIndex, 1) );
      }
    }
  }

  private async onReaction(msgID: string, reactionID: string, userID: string,
      contentID: string) {
    const [dayStr, msgIndex] = this.findMessage(msgID);
    if (!dayStr) {
      console.log("no message found for reaction");
      return;
    }

    try {
      const reaction = await dbServiceFactory().getReaction(reactionID);
      
      if (!reaction) {
        console.error("reaction not found for ID:", reactionID, "msgID:", msgID, "from", userID,
          "contentID:", contentID);
        return;
      }
      const reactionUser = {
        reactionID: reactionID,
        userID: userID,
        firstname: reaction.firstname,
        lastname: reaction.lastname
      };
      this.reloadDay(dayStr, newArr => {
        const msg = newArr[msgIndex] as ContentMessage;
        if (!msg.reactions) { msg.reactions = {}; }
        if (!msg.reactions[contentID]) {
          if (!reaction.users) { reaction.users = []; }
          reaction.users.push(reactionUser);
          msg.reactions[contentID] = reaction;
        } else {
          const reaction = msg.reactions[contentID];
          if (!reaction.users) { reaction.users = []; }
          const index = reaction.users.findIndex(u => u.userID === userID);
          if (index < 0) {
            reaction.users.push(reactionUser);
          }
        }
      });
    } catch (err) {
      console.error("Error fetching reaction:", err);
    }
  }

  private async onRemoveReaction(msgID: string, contentID: string, userID: string) {
    const [dayStr, msgIndex] = this.findMessage(msgID);
    if (!dayStr) { return; }

    this.reloadDay(dayStr, newArr => {
      const msg = newArr[msgIndex] as ContentMessage;
      if (!msg.reactions) { return; }
      if (!msg.reactions[contentID]) { return; }
      const reaction = msg.reactions[contentID];
      if (!reaction.users) { return; }
      const index = reaction.users.findIndex(u => u.userID === userID);
      if (index >= 0) {
        reaction.users.splice(index, 1);
        if (reaction.users.length <= 0) {
          delete msg.reactions[contentID];
        }
      }
    });
  }

  private findMessage (msgID: string): [string | null, number] {
    let dayStr = "";
    let msgIndex = -1;
    for (const d in this.state.dayMap) {
      const day = this.state.dayMap[d];
      msgIndex = day.messages.findIndex(m => m._id === msgID);
      if (msgIndex >= 0) {
        dayStr = d;
        break;
      }
    }
    if (msgIndex >= 0) {
      return [dayStr, msgIndex];
    }
    return [null, -1];
  }

  private reloadDay (dayStr: string, update?: (newArr: Array<MessageData>) => void) {
    const day = this.state.dayMap[dayStr];
    const newArr = Array.from(day.messages);
    update && update(newArr);
    if (newArr.length <= 0) {
      delete this.state.dayMap[dayStr];
      this.setState({dayMap: this.state.dayMap});
    } else {
      this.setState({dayMap: {
        ...this.state.dayMap,
        [dayStr]: {
          startOfDay: day.startOfDay,
          messages: newArr
        }
      }});
    }
  }

  private onShowEmojiPicker (anchor: HTMLElement, msg: ContentMessage) {
    this.setState({openEmojiPicker: true, anchorEl: anchor, reactingMessage: msg});
  }

  private async choseReaction (emoji: EmojiSelection) {
    if (!this.state.reactingMessage || !this.props.addReaction) {
      this.setState({openEmojiPicker: false, reactingMessage: null});
      return;
    }
    const [dayStr, ] = this.findMessage(this.state.reactingMessage._id);
    if (!dayStr) {
      this.setState({openEmojiPicker: false, reactingMessage: null});
      return;
    }

    
    if (!this.state.reactingMessage.reactions) {
      this.state.reactingMessage.reactions = {};
    }

    const reactions = this.state.reactingMessage.reactions;
    const existing = reactions[emoji.id];
    
    if (existing && existing.users &&
        existing.users.findIndex(u => u.userID === this.props.myUserID) >= 0) {
      this.setState({openEmojiPicker: false, reactingMessage: null});
      return;
    }

    const { content, contentType } = emojiSelectionToContent(emoji);

    try {
      await this.props.addReaction(
          this.state.reactingMessage, emoji.id, contentType, content);
      
      this.reloadDay(dayStr);
    } catch (err) {
      console.error("Error adding reaction:", err);
      this.props.onError((err as Error).message);
    } finally {
      this.setState({openEmojiPicker: false, reactingMessage: null});
    }
  }

  private async toggleReaction(msg: ContentMessage, reaction: Reaction) {
    const [dayStr,] = this.findMessage(msg._id);
    if (!dayStr) {
      return;
    }

    if (!reaction.users) { reaction.users = []; }

    const users = reaction.users;
    const index = users.findIndex(u => u.userID === this.props.myUserID);

    reaction.loading = true;
    this.reloadDay(dayStr);
    try {
      if (index >= 0) {
        const myReaction = users[index];
        this.props.removeReaction && await this.props.removeReaction(msg, myReaction.reactionID, reaction.contentID, reaction.frameID);
      } else {
        this.props.addReaction && await this.props.addReaction(msg, reaction.contentID, reaction.contenttype, reaction.content);
      }
    } catch (err) {
      console.error("Error saving reaction:", err);
      this.props.onError((err as Error).message);
    } finally {
      reaction.loading = false;
      this.reloadDay(dayStr);
    }
  }

  render (): React.ReactNode {
    if (this.props.loadingGroup) {
      return <div className="group-loading" key="group-loading">
        <CircularProgress size={30} />
      </div>;
    }
    const days = Object.keys(this.state.dayMap);
    days.sort((a, b) => {
      const aDay = this.state.dayMap[a];
      const bDay = this.state.dayMap[b];
      if (aDay.startOfDay < bDay.startOfDay) {
        return -1;
      } else if (bDay.startOfDay < aDay.startOfDay) {
        return 1;
      }
      return 0;
    });

    const haveMessageWritePerm = (this.context as GroupNavContext).can(PermissionLevel.WRITE, "messages");
    return <>
    {this.state.loadingMessages && <div className="message-loading" key="group-loading">
        <CircularProgress size={30} />
      </div>}
    {days.map(dayStr => {
      const msgDay = this.state.dayMap[dayStr];
      // already set TZ for startOfDay
      const nodes: Array<React.ReactNode> = [
        <header key={dayStr} className="day-header">
          <span>{msgDay.startOfDay.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}</span>
        </header>
      ];
      msgDay.messages.forEach(msg => {
        switch (msg.msgtype) {
          case MessageType.USER_JOINED:
          case MessageType.DEV_JOINED:
            nodes.push(
              <DeviceJoinedComponent key={msg._id}
                msg={msg as DeviceJoinedMessage}
                displayTZ={this.props.authContext.tz()}
                doRemoveLocally={this.props.doRemoveMessageLocally} />
            );
            break;
          case MessageType.USER_ADDED:
          case MessageType.DEV_ADDED:
            nodes.push(
              <DevicesAddedComponent key={msg._id}
                displayTZ={this.props.authContext.tz()}
                msg={msg as DevicesAddedMessage} />
            );
            break;
          case MessageType.HISTORY:
            nodes.push(
              <HistoryPreservationComponent key={msg._id}
                displayTZ={this.props.authContext.tz()}
                msg={msg as HistoryPreservationMessage} />
            );
            break;
          default:
            nodes.push(
              <MessageDisplayComponent key={msg._id}
                inThread={false}
                canWriteMessages={haveMessageWritePerm}
                msg={msg as ContentMessage}
                displayTZ={this.props.authContext.tz()}
                maintainHover={msg._id == this.state.reactingMessage?._id}
                myUserID={this.props.myUserID}
                doRemoveMessage={this.props.doRemoveMessage}
                doRemoveLocally={this.props.doRemoveMessageLocally}
                doThread={this.props.doThread}
                doDownloadFile={this.props.doDownloadFile}
                showEmojiPicker={this.onShowEmojiPicker}
                doToggleReaction={this.toggleReaction} />
            );
        }
      });
      return nodes;
    })}
    <Popper open={this.state.openEmojiPicker} anchorEl={this.state.anchorEl}
        disablePortal placement="auto-end">
      <ClickAwayListener onClickAway={() => this.setState({openEmojiPicker: false, reactingMessage: null})}>
        <div>
          <Picker custom={CustomEmoji} onEmojiSelect={this.choseReaction} />
        </div>
      </ClickAwayListener>
    </Popper>
    </>;
  }
  // <Button><i className="fas fa-phone" /></Button>
}

interface DeviceJoinedComponentProps {
  msg: DeviceJoinedMessage,
  displayTZ: string,
  doRemoveLocally?(_id: string, frameID: string | undefined, threadID: string | undefined): void,
}

function DeviceJoinedComponent(props: DeviceJoinedComponentProps): React.ReactElement<any, any> {
  const { msg } = props;
  const [acctMember, setAcctMember] = React.useState<Membership | null>(null);

  React.useEffect(function () {
      authServiceFactory().memberFor(msg.userID).then( mem => {
      setAcctMember(mem);
      if (updateCachedMemberNameAsNeeded(msg, mem)) {
        return;
      }
      const dbService = dbServiceFactory();
      return dbService.updateMessage(msg._id, msg.accountID, message => {
        if (message) {
          if (msg.firstname) { message.firstname = msg.firstname; }
          if (msg.lastname) { message.lastname = msg.lastname; }
        }
        return bluebird.resolve(message);
      });
    }).then(null, err => {
      console.error("Error getting membership:", err);
    });
  }, []);

  const firstname = acctMember?.firstname || msg.firstname || "";
  const lastname = acctMember?.lastname || msg.lastname || "";

  const ts = msg.created;
  const timestamp = moment.isMoment(ts) ? DateTime.fromJSDate(ts.toDate()) :
    ts instanceof Date ? DateTime.fromJSDate(ts) :
    ts;
  if (props.displayTZ) {
    timestamp.setZone(props.displayTZ);
  }

  return <div className="group-joined-container">
    <SCAvatar size="small" text={acctMember?.avatarText || "XX"} url={acctMember?.avatarUrl} />
    <p>
      {firstname || lastname ?
        <span className="message-actor">{firstname} {lastname}</span> :
        <span className="message-actor">unknown</span>}
        <span> joined the Group on </span>
        <span className="device-name">{msg.deviceName}</span>
    </p>
    <p><span className="creation-time">
        {timestamp.toFormat("h:mma")}
      </span></p>
    
    {props.doRemoveLocally && <ButtonGroup className="message-actions"
        size="small" variant="contained" color="secondary"
        aria-label="outlined primary button group">
      
      
        <Button className="delete-btn"
            onClick={() => props.doRemoveLocally && props.doRemoveLocally(msg._id, msg.frameID, msg.threadID)}
            disableRipple disableTouchRipple>
          <DeleteIcon />
        </Button>
    </ButtonGroup>}
  </div>;
}


interface DevicesAddedComponentProps {
  msg: DevicesAddedMessage,
  displayTZ: string,
  doRemoveMessage?(_id: string): void
}

function DevicesAddedComponent(props: DevicesAddedComponentProps): React.ReactElement<any, any> {
  const { msg } = props;
  const [acctMember, setAcctMember] = React.useState<Membership | null>(null);
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  const [isArchiver, setIsArchiver] = React.useState<boolean>(false);
  const [nameList, setNameList] = React.useState<string>(msg.namesList || "");

  React.useEffect(function () {
    const userSet = new Set<string>();
    for (const dev of msg.devices) {
      userSet.add(dev.userID);
    }
    const addedOwnDevice = userSet.has(msg.userID);
    userSet.add(msg.userID);
    if (msg.devices.length === 1 && msg.devices[0].userID === msg.groupID) {
      setIsArchiver(true);
    }

    authServiceFactory().membersFor(userSet).then( memberMap => {
      let noUpdateNeeded = true;

      if (memberMap[msg.userID]) {
        const myMembership = memberMap[msg.userID];
        setAcctMember(myMembership);
        noUpdateNeeded = updateCachedMemberNameAsNeeded(msg, myMembership);
      }
      const members: Array<Membership> = [];
      let i = 0;
      for (const userID of userSet) {
        if (userID in memberMap) {
          members.push(memberMap[userID]);
          i++;
          if (i >= 5) {break;}
        }
      }
      setMembers(members);
      if (msg.namesList && noUpdateNeeded) { return; }
      if (!addedOwnDevice) {
        userSet.delete(msg.userID);
      }
      msg.userSet = userSet;

      i = 0;
      let text = "";
      for (const userID of userSet) {
        const mem = memberMap[userID];
        if (!mem) { continue; }
        const firstname = mem.firstname || "";
        const lastname = mem.lastname || "";
        const name = mem.userID === msg.userID ? "zirself" : firstname + " " + lastname;

        if (i === 0) {
          text = name;
          i++;
          continue;
        }
        if (i === userSet.size - 1) {
          if (i > 1) {
            // more than 2 members
            text += ", ";
          }
          text += ` and ${name}`;
        } else if (i >= 2) {
          i = userSet.size - i;
          text += `and ${i} more people`;
          break;
        } else {
          text += `, ${name}`;
        }
        i++;
      }
      if (text) {
        msg.namesList = text;
        setNameList(text);
      }
      const dbService = dbServiceFactory();
      return dbService.updateMessage(msg._id, msg.accountID, message => {
        if (message) {
          if (msg.firstname) { message.firstname = msg.firstname; }
          if (msg.lastname) { message.lastname = msg.lastname; }
          if (text) {(message as DevicesAddedMessage).namesList = text; }
          (message as DevicesAddedMessage).userSet = userSet;
        }
        return bluebird.resolve(message);
      });

    }).then(null, err => {
      console.error("Error getting membership:", err);
    });
  }, []);

  const firstname = acctMember?.firstname || msg.firstname || "";
  const lastname = acctMember?.lastname || msg.lastname || "";

  const ts = msg.created;
  const timestamp = moment.isMoment(ts) ? DateTime.fromJSDate(ts.toDate()) :
    ts instanceof Date ? DateTime.fromJSDate(ts) :
    ts;
  if (props.displayTZ) {
    timestamp.setZone(props.displayTZ);
  }

  /*<div key={member.userID} className="small-avatar">
          <span>{member.avatarText}</span>
        </div>*/
  return <div className="group-joined-container">
    <div className="avatar-set">
      {members.map(member =>
        <SCAvatar key={member.userID} size="small" text={member.avatarText || "XX"}
          url={member.avatarUrl} />
      )}
    </div>
    <p>
      {firstname || lastname ?
        <span className="message-actor">{firstname} {lastname}</span> :
        <span className="message-actor">unknown</span>}
        {isArchiver ? <span> enabled archiving for this group</span> : <>
          <span> added {msg.devices.length > 1 ? "devices" : "a device"} for </span>
          <span>{nameList}</span>
        </>
      }
    </p>
    <p><span className="creation-time">
        {timestamp.toFormat("h:mma")}
      </span></p>
  </div>;
  /*<ButtonGroup className="message-actions"
        size="small" variant="outlined" color="primary"
        aria-label="outlined primary button group">
      {props.doRemoveMessage &&
          <Button onClick={() => props.doRemoveMessage && props.doRemoveMessage(msg._id)}>
            disableRipple disableTouchRipple
            <DeleteForeverIcon />
          </Button>}
    </ButtonGroup>*/
}

interface HistoryPreservationComponentProps {
  msg: HistoryPreservationMessage,
  displayTZ: string
}
function HistoryPreservationComponent(props: HistoryPreservationComponentProps): React.ReactElement {
  const { msg, displayTZ } = props;
  const [acctMember, setAcctMember] = React.useState<Membership | null>(null);

  React.useEffect(() => {
    authServiceFactory().memberFor(msg.userID).then(mem => {
      setAcctMember(mem);
      updateCachedMemberNameAsNeeded(msg, mem);
    }).catch(err => {
      console.error("Error getting membership:", err);
    });
  }, []);

  const firstname = acctMember?.firstname || msg.firstname || "";
  const lastname = acctMember?.lastname || msg.lastname || "";

  const timestamp = moment(msg.created);
  if (displayTZ) {
    timestamp.tz(displayTZ);
  }

  return (
    <div className="group-joined-container">
      <SCAvatar text={acctMember?.avatarText || "XX"} url={acctMember?.avatarUrl} size="small" />
      <div className="history-preservation-content">
        <p>
          <span className="message-actor">{firstname} {lastname}</span>
          <span> {msg.isPreserved ? "enabled" : "disabled"} history preservation for this group</span>
        </p>
        <p>
          <span className="creation-time">{timestamp.format("h:mma")}</span>
        </p>
      </div>
    </div>
  );
}
